/**
 * Dynamic GA4 Measurement ID Resolver
 * Determines the GA4 Measurement ID based on the exact current domain
 */

interface GA4Config {
  measurementId: string;
  debugMode: boolean;
}

/**
 * Domain to GA4 Measurement ID mapping
 * Uses exact URL matching - add all domains explicitly
 * Update these IDs based on your GA4 property configuration per environment
 */
const DOMAIN_MAPPINGS: Record<string, GA4Config> = {
  // Production
  'skillzen.ai': {
    measurementId: 'G-FNJLMNTW7R',
    debugMode: false
  },
  'www.skillzen.ai': {
    measurementId: 'G-FNJLMNTW7R',
    debugMode: false
  },

  // Nightly/Staging environments
  'nightly.skillzen.ai': {
    measurementId: 'G-FNJLMNTW7R',
    debugMode: true
  },
  'staging.skillzen.ai': {
    measurementId: 'G-FNJLMNTW7R',
    debugMode: true
  },
  'dev.skillzen.ai': {
    measurementId: 'G-FNJLMNTW7R',
    debugMode: true
  },
  'demo.skillzen.ai': {
    measurementId: 'G-FNJLMNTW7R',
    debugMode: true
  },

  // Preview deployments (Cloudflare Pages)
  'preview.skillzen.ai': {
    measurementId: 'G-FNJLMNTW7R',
    debugMode: true
  },

  // Local development
  'localhost': {
    measurementId: 'G-FNJLMNTW7R',
    debugMode: true
  },
  '127.0.0.1': {
    measurementId: 'G-FNJLMNTW7R',
    debugMode: true
  }
};

/**
 * Default fallback configuration (when no exact match found)
 */
const DEFAULT_CONFIG: GA4Config = {
  measurementId: '',
  debugMode: true
};

/**
 * Get GA4 configuration based on current environment
 */
export function getGA4Config(): GA4Config {
  // Priority 1: Check for explicit environment variable (not AUTO)
  const envMeasurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;

  if (envMeasurementId && envMeasurementId !== 'AUTO') {
    return {
      measurementId: envMeasurementId,
      debugMode: import.meta.env.VITE_ENVIRONMENT !== 'production'
    };
  }

  // Priority 2: Exact match from current hostname (when AUTO or not set)
  const hostname = window.location.hostname;

  console.log('[GA4] Resolving Measurement ID for hostname:', hostname);

  // Exact match only
  if (DOMAIN_MAPPINGS[hostname]) {
    console.log('[GA4] Found exact match for hostname:', hostname);
    return DOMAIN_MAPPINGS[hostname];
  }

  // No match found - return empty config (GA4 will not initialize)
  console.warn('[GA4] No domain mapping found for:', hostname);
  return DEFAULT_CONFIG;
}

/**
 * Get the GA4 Measurement ID
 */
export function getGA4MeasurementId(): string {
  const config = getGA4Config();
  return config.measurementId;
}

/**
 * Check if debug mode should be enabled
 */
export function isGA4DebugMode(): boolean {
  const config = getGA4Config();
  return config.debugMode;
}

/**
 * Log current GA4 configuration (for debugging)
 */
export function logGA4Config(): void {
  const config = getGA4Config();

  console.group('[GA4] Configuration');
  console.log('Hostname:', window.location.hostname);
  console.log('Measurement ID:', config.measurementId || '(not configured)');
  console.log('Debug Mode:', config.debugMode);
  console.groupEnd();
}
